/**
 * /admin/triage — Rule-suggestion Triage view (PRP-RULES-TRIAGE Slice 3).
 *
 * Shows the 842 LLM-judged proposed rules with verdict / confidence / rationale /
 * guards, sorted VALID_CONDITIONAL-first (guards need the founder's eye), and lets
 * the founder approve (-> active) or reject (-> retired) per rule. Separate from the
 * multi-reviewer Rule Review Console; this is the AI-triage axis.
 *
 * Access: super_admin only.
 */

import * as React from 'react';
import { Navigate } from 'react-router-dom';
import { Check, ShieldAlert, X } from 'lucide-react';

import useAuthStore from '../stores/authStore';
import { TriageAPI } from '../lib/triageApi';
import type { TriageRule, TriageSummary, Verdict } from '../lib/triageApi';
import { cn, getErrorMessage } from '../lib/utils';
import RuleChatPanel from '../components/RuleChatPanel';

const SUPER = 'super_admin';

const VERDICT_META: Record<Verdict, { label: string; cls: string }> = {
  VALID_CONDITIONAL: { label: 'Conditional', cls: 'bg-amber-100 text-amber-800' },
  VALID_CAUSAL: { label: 'Causal', cls: 'bg-emerald-100 text-emerald-800' },
  WRONG_PAIRING: { label: 'Wrong pairing', cls: 'bg-rose-100 text-rose-800' },
  PLAUSIBLE_WORKFLOW: { label: 'Plausible', cls: 'bg-sky-100 text-sky-800' },
  SPURIOUS: { label: 'Spurious', cls: 'bg-slate-200 text-slate-700' },
  MISSING_CATALOG: { label: 'No catalog', cls: 'bg-slate-200 text-slate-500' },
};

const FILTER_ORDER: Verdict[] = [
  'VALID_CONDITIONAL',
  'VALID_CAUSAL',
  'WRONG_PAIRING',
  'PLAUSIBLE_WORKFLOW',
  'SPURIOUS',
];

function guardText(rule: TriageRule): string | null {
  const g = rule.guards;
  if (!g) return null;
  const parts: string[] = [];
  if (g.unless_present?.length) parts.push(`unless ${g.unless_present.join(', ')} present`);
  if (g.only_if_present?.length) parts.push(`only if ${g.only_if_present.join(', ')} present`);
  return parts.length ? `Fires ${parts.join(' and ')}` : null;
}

function hint(rule: TriageRule): string | null {
  const h = rule.triage_meta?.correct_target_hint;
  return typeof h === 'string' ? h : null;
}

const AdminTriagePage: React.FC = () => {
  const { roles } = useAuthStore();
  const hasAccess = roles.includes(SUPER);

  const [summary, setSummary] = React.useState<TriageSummary | null>(null);
  const [rules, setRules] = React.useState<TriageRule[]>([]);
  const [filter, setFilter] = React.useState<Verdict | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [acting, setActing] = React.useState<string | null>(null);
  const [toast, setToast] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    if (!hasAccess) return;
    setLoading(true);
    setError(null);
    try {
      const [sum, list] = await Promise.all([
        TriageAPI.summary(),
        TriageAPI.listRules(filter ?? undefined),
      ]);
      setSummary(sum);
      setRules(list.rules);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [hasAccess, filter]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const decide = async (rule: TriageRule, action: 'approve' | 'reject') => {
    setActing(rule.id);
    try {
      const res = await TriageAPI.decide(rule.id, action);
      // Optimistically drop the row from the queue.
      setRules((prev) => prev.filter((r) => r.id !== rule.id));
      if (action === 'reject') {
        setToast('Rejected — rule retired.');
      } else if (res.activated) {
        setToast('Approved & activated — this rule now fires in estimates.');
      } else {
        setToast('Approved.');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActing(null);
    }
  };

  if (!hasAccess) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-800">
          <ShieldAlert className="h-5 w-5 text-amber-600" />
          Rule Suggestion Triage
        </h1>
        <p className="text-sm text-slate-500">
          LLM-judged rule suggestions. Approving a rule <strong>activates it immediately</strong> — it
          starts firing in estimates, with its guards enforced (conditional rules, shown first, fire
          only when their guard conditions are met). Rejecting retires the rule.
        </p>
      </header>

      <RuleChatPanel />

      {toast && (
        <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          {toast}
        </div>
      )}

      {summary && (
        <section className="flex flex-wrap gap-2 text-xs">
          <span className="rounded bg-slate-100 px-2 py-1 text-slate-600">
            {summary.by_stage?.judged_stage2 ?? 0} judged · {summary.by_stage?.killed_stage1 ?? 0} filtered ·{' '}
            {summary.total_proposed} total
          </span>
          <button
            onClick={() => setFilter(null)}
            className={cn(
              'rounded px-2 py-1',
              filter === null ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600',
            )}
          >
            All
          </button>
          {FILTER_ORDER.map((v) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              className={cn(
                'rounded px-2 py-1',
                filter === v ? 'bg-slate-800 text-white' : VERDICT_META[v].cls,
              )}
            >
              {VERDICT_META[v].label} ({summary.by_verdict?.[v] ?? 0})
            </button>
          ))}
        </section>
      )}

      {error && (
        <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}

      <section className="overflow-x-auto rounded-lg border bg-white shadow-sm">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-left text-slate-500">
            <tr>
              <th className="px-3 py-2 font-medium">Verdict</th>
              <th className="px-3 py-2 font-medium">Conf</th>
              <th className="px-3 py-2 font-medium">Rule</th>
              <th className="px-3 py-2 font-medium">Rationale / guard</th>
              <th className="px-3 py-2 font-medium">Source</th>
              <th className="px-3 py-2 text-right font-medium">Decision</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-slate-400">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rules.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-slate-400">
                  Nothing to review here.
                </td>
              </tr>
            )}
            {rules.map((rule) => {
              const vm = rule.verdict ? VERDICT_META[rule.verdict] : null;
              const g = guardText(rule);
              const h = hint(rule);
              return (
                <tr key={rule.id} className="border-t align-top hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <span className={cn('rounded px-1.5 py-0.5 font-medium', vm?.cls)}>
                      {vm?.label ?? rule.verdict}
                    </span>
                  </td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">
                    {rule.verdict_confidence ?? '—'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-mono text-[11px] text-slate-700">{rule.name}</div>
                    {rule.description && (
                      <div className="mt-0.5 max-w-md text-slate-500">{rule.description}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 max-w-md text-slate-600">
                    {rule.verdict_rationale}
                    {g && <div className="mt-1 font-medium text-amber-700">⚑ {g}</div>}
                    {h && (
                      <div className="mt-1 text-rose-700">
                        → suggested: <span className="font-mono">{h}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-400">
                    {rule.source_corpus}
                    {rule.support != null && ` · n=${rule.support}`}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <button
                        disabled={acting === rule.id}
                        onClick={() => decide(rule, 'approve')}
                        title={
                          rule.verdict === 'VALID_CONDITIONAL'
                            ? 'Approve & activate — fires with its guards enforced at runtime'
                            : 'Approve & activate — fires in estimates immediately'
                        }
                        className="inline-flex items-center gap-1 rounded bg-emerald-600 px-2 py-1 text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        <Check className="h-3 w-3" />{' '}
                        Approve & activate
                      </button>
                      <button
                        disabled={acting === rule.id}
                        onClick={() => decide(rule, 'reject')}
                        className="inline-flex items-center gap-1 rounded bg-slate-200 px-2 py-1 text-slate-700 hover:bg-slate-300 disabled:opacity-50"
                      >
                        <X className="h-3 w-3" /> Reject
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default AdminTriagePage;
